import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { newsList } from "@/mocks/news";
import useDocumentTitle from "@/hooks/useDocumentTitle";
import NewsCard from "@/components/feature/NewsCard";
import Breadcrumb from "@/components/base/Breadcrumb";
import EmptyState from "@/components/base/EmptyState";

export default function NewsDetail() {
  const { id } = useParams();
  const [copied, setCopied] = useState(false);

  const article = newsList.find((n) => n.id === id);

  useDocumentTitle(
    article ? `${article.title} - 漫蛙2动漫资讯` : "资讯不存在 - 漫蛙2动漫资讯",
    article ? article.summary : "该资讯不存在或已下架。"
  );

  const related = useMemo(() => {
    if (!article) return [];
    const sameCategory = newsList.filter((n) => n.id !== article.id && n.category === article.category);
    const others = newsList.filter((n) => n.id !== article.id && n.category !== article.category);
    return [...sameCategory, ...others].slice(0, 3);
  }, [article]);

  if (!article) {
    return (
      <div className="page-wrap py-16">
        <EmptyState
          icon="ri-file-unknow-line"
          title="这篇资讯不存在或已下架"
          description="内容可能被移动或删除，你可以返回资讯列表查看其他内容。"
          actionLabel="返回资讯列表"
          actionTo="/news"
        />
      </div>
    );
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="pb-4">
      <div className="page-wrap py-6 md:py-8">
        <Breadcrumb items={[{ label: "动漫资讯", to: "/news" }, { label: article.category }]} />
      </div>

      <article className="page-wrap grid grid-cols-1 gap-10 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <span className="inline-block rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold text-primary-700">
            {article.category}
          </span>
          <h1 className="mt-4 text-2xl font-extrabold leading-snug text-foreground-950 md:text-3xl">{article.title}</h1>
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {article.date}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line"></i>
              {article.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-price-tag-3-line"></i>
              {article.source}
            </span>
          </div>

          <div className="mt-6 overflow-hidden rounded-lg bg-background-200">
            <img
              src={article.cover}
              alt={`${article.title} 封面配图`}
              title={`${article.title} - 漫蛙2动漫资讯`}
              className="h-auto w-full object-cover object-top"
            />
          </div>

          <div className="mt-8 space-y-5">
            {article.content.map((paragraph, index) => (
              <p key={index} className="text-[15px] leading-[1.9] text-foreground-800">
                {paragraph}
              </p>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap items-center gap-2 border-t border-background-200 pt-6">
            <span className="text-sm text-foreground-500">标签：</span>
            {article.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-foreground-600 transition-colors hover:bg-primary-100 hover:text-primary-700"
              >
                #{tag}
              </Link>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-background-50 p-4">
            <span className="text-sm font-medium text-foreground-700">分享这篇资讯</span>
            <button
              type="button"
              onClick={handleCopy}
              className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className={copied ? "ri-check-line" : "ri-link"}></i>
              {copied ? "链接已复制" : "复制链接"}
            </button>
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md border border-background-200 px-4 py-2 text-sm text-foreground-600">
              <i className="ri-share-forward-line"></i>
              分享到社交平台（演示）
            </span>
          </div>
        </div>

        <aside className="lg:col-span-1">
          <h2 className="text-lg font-bold text-foreground-950">相关推荐</h2>
          <div className="mt-4 flex flex-col gap-3">
            {related.map((item) => (
              <NewsCard key={item.id} item={item} layout="horizontal" />
            ))}
          </div>

          <div className="mt-8 rounded-lg bg-secondary-900 p-5">
            <h3 className="text-base font-bold text-background-50">参与话题讨论</h3>
            <p className="mt-2 text-sm leading-relaxed text-background-300">
              对这篇内容有想法？到社区里发表你的观点，和同好一起聊聊。
            </p>
            <Link
              to="/community"
              className="mt-4 inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              前往社区
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </aside>
      </article>
    </div>
  );
}